import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ArrowLeft, CheckCircle2, XCircle, Clock, FileText, Edit, Download } from 'lucide-react';
import PTWWorkflowActions from '../../components/PTWWorkflowActions';
import PTWTimeline from '../../components/PTWTimeline';
import { AuditTrailViewer } from '../../components/AuditTrail';
import { useAlert } from '../../context/AlertContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PTWDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [permit, setPermit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'audit'

  useEffect(() => {
    fetchPermit();
  }, [id]);

  const fetchPermit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/permits/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermit(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load permit');
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/permits/${id}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `PTW_${permit.permit_number.replace('/', '-')}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setExporting(false);
    } catch (err) {
      console.error('Export failed:', err);
      showError('Failed to export PDF. Please try again.');
      setExporting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Pending Approval': 'bg-yellow-100 text-yellow-800',
      'Approved': 'bg-blue-100 text-blue-800',
      'Active': 'bg-green-100 text-green-800',
      'Suspended': 'bg-red-100 text-red-800',
      'Closed': 'bg-purple-100 text-purple-800',
      'Rejected': 'bg-red-100 text-red-800',
      'Expired': 'bg-gray-100 text-gray-800',
      'Extension Requested': 'bg-orange-100 text-orange-800',
      'HSE Verified': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !permit) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-5xl mx-auto">
          <Alert variant="destructive">
            <AlertDescription>{error || 'Permit not found'}</AlertDescription>
          </Alert>
          <Button onClick={() => navigate('/ptw')} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to List
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/ptw')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to List
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{permit.permit_number}</h1>
              <p className="text-gray-600 mt-1">{permit.permit_type} Permit</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(permit.status)}`}>
                {permit.status}
              </span>
              <Button
                onClick={handleExportPDF}
                disabled={exporting}
                className="bg-green-600 hover:bg-green-700"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export PDF
              </Button>
              {permit.status === 'Draft' && (
                <Button
                  onClick={() => navigate(`/ptw/edit/${permit.id}`)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Workflow Actions */}
        <div className="mb-6">
          <PTWWorkflowActions permit={permit} onUpdate={fetchPermit} />
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('details')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'audit'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Audit History
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' ? (
          <>
        {/* Main Details Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Permit Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-semibold text-gray-600">Work Description</label>
                <p className="text-gray-900 mt-1">{permit.work_description}</p>
              </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-600">Location</label>
                <p className="text-gray-900 mt-1">{permit.location}</p>
              </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-600">Applicant</label>
                <p className="text-gray-900 mt-1">{permit.applicant_name}</p>
              </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-600">Work Schedule</label>
                <p className="text-gray-900 mt-1">
                  {new Date(permit.work_start_date).toLocaleString()} 
                  <br />
                  <span className="text-gray-500">to</span>
                  <br />
                  {new Date(permit.work_end_date).toLocaleString()}
                </p>
              </div>

              {permit.contractor_company && (
                <div>
                  <label className="text-sm font-semibold text-gray-600">Contractor</label>
                  <p className="text-gray-900 mt-1">{permit.contractor_company}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-semibold text-gray-600">Created</label>
                <p className="text-gray-900 mt-1">
                  {new Date(permit.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hazards & Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-red-700">Identified Hazards</CardTitle>
            </CardHeader>
            <CardContent>
              {permit.hazards_identified && permit.hazards_identified.length > 0 ? (
                <ul className="space-y-2">
                  {permit.hazards_identified.map((hazard, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-red-600 mt-1">⚠️</span>
                      <span className="text-gray-900">{hazard}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500">No hazards identified</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-green-700">Control Measures</CardTitle>
            </CardHeader>
            <CardContent>
              {permit.control_measures && permit.control_measures.length > 0 ? (
                <ul className="space-y-2">
                  {permit.control_measures.map((control, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <span className="text-gray-900">{control}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500">No control measures specified</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Safety Checklist */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Safety Checklist</CardTitle>
          </CardHeader>
          <CardContent>
            {permit.safety_checklist && permit.safety_checklist.length > 0 ? (
              <div className="space-y-3">
                {permit.safety_checklist.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    {item.checked ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-400 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`font-semibold ${item.checked ? 'text-gray-900' : 'text-gray-500'}`}>
                        {item.question}
                      </p>
                      {item.remarks && (
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-semibold">Remarks:</span> {item.remarks}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No checklist items</p>
            )}
          </CardContent>
        </Card>

        {/* Workers */}
        {permit.workers && permit.workers.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Authorized Workers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {permit.workers.map((worker, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-2 ${
                      worker.has_valid_certifications
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">{worker.employee_name}</p>
                        {worker.missing_certifications && worker.missing_certifications.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm font-semibold text-red-700">Missing Certifications:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {worker.missing_certifications.map((cert, idx) => (
                                <span key={idx} className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                                  {cert}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      {worker.has_valid_certifications ? (
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Gas Testing Results */}
        {permit.gas_test_readings && permit.gas_test_readings.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Gas Testing Results</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {permit.gas_test_readings.map((reading, index) => (
                  <div key={index} className="p-4 bg-blue-50 rounded-lg">
                    <p className="font-semibold text-gray-900 mb-3">
                      Reading #{index + 1} - {new Date(reading.test_time).toLocaleString()}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">O₂ Level:</span>
                        <p className="font-semibold text-gray-900">{reading.oxygen_level}%</p>
                      </div>
                      <div>
                        <span className="text-gray-600">LEL:</span>
                        <p className="font-semibold text-gray-900">{reading.lel_level}%</p>
                      </div>
                      <div>
                        <span className="text-gray-600">H₂S:</span>
                        <p className="font-semibold text-gray-900">{reading.h2s_level} ppm</p>
                      </div>
                      <div>
                        <span className="text-gray-600">CO:</span>
                        <p className="font-semibold text-gray-900">{reading.co_level} ppm</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">Tested by:</span> {reading.tester_name}
                      </p>
                      {reading.remarks && (
                        <p className="text-sm text-gray-700 mt-1">
                          <span className="font-semibold">Remarks:</span> {reading.remarks}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Isolation Points */}
        {permit.isolation_points && permit.isolation_points.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Isolation/LOTO Points</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {permit.isolation_points.map((point, index) => (
                  <div key={index} className="p-4 bg-orange-50 rounded-lg">
                    <p className="font-semibold text-gray-900 mb-3">
                      Isolation Point #{index + 1}
                    </p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Equipment Tag:</span>
                        <p className="font-semibold text-gray-900">{point.equipment_tag}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Isolation Type:</span>
                        <p className="font-semibold text-gray-900">{point.isolation_type}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Lock Number:</span>
                        <p className="font-semibold text-gray-900">{point.lock_number}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Verified By:</span>
                        <p className="font-semibold text-gray-900">{point.isolation_verified_by}</p>
                      </div>
                    </div>
                    {point.remarks && (
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">Remarks:</span> {point.remarks}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Emergency Contacts */}
        {(permit.emergency_contact_name || permit.rescue_plan_available) && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Emergency Information</CardTitle>
            </CardHeader>
            <CardContent>
              {permit.emergency_contact_name && (
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-600">Emergency Contact</p>
                  <p className="text-gray-900 mt-1">
                    {permit.emergency_contact_name}
                    {permit.emergency_contact_number && ` - ${permit.emergency_contact_number}`}
                  </p>
                </div>
              )}
              
              {permit.rescue_plan_available && (
                <div>
                  <p className="text-sm font-semibold text-gray-600">Rescue Plan</p>
                  <div className="flex items-center gap-2 mt-1">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span className="text-gray-900">Rescue plan available</span>
                  </div>
                  {permit.rescue_plan_description && (
                    <p className="text-gray-700 mt-2 p-3 bg-gray-50 rounded">
                      {permit.rescue_plan_description}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Status Timeline */}
        <PTWTimeline permit={permit} />
          </>
        ) : (
          /* Audit History Tab */
          <Card>
            <CardContent className="pt-6">
              <AuditTrailViewer
                entityType="permit"
                entityId={id}
                title="Permit Audit History"
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PTWDetail;
